import React, { useMemo } from 'react';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    ScatterChart,
    Scatter,
    ZAxis
} from 'recharts';
import { AlertCircle, Mic, BarChart3 } from 'lucide-react';
import styles from './ClarityAnalysis.module.css';
import InfoModal from '../InfoModal/InfoModal';

const CLARITY_THRESHOLD = 0.75; // 75% confidence threshold

const ClarityAnalysis = ({ segments }) => {
    const { clarityData, stats } = useMemo(() => {
        if (!segments?.length) return { clarityData: [], stats: {} };

        const data = segments
            .filter(segment => segment.type === 'word' && !segment.tags?.includes('punctuation'))
            .map((segment, index) => ({
                word: segment.content,
                confidence: segment.confidence || 1,
                index,
                lowClarity: (segment.confidence || 1) < CLARITY_THRESHOLD,
                tooltipContent: `${segment.content} (${((segment.confidence || 1) * 100).toFixed(1)}% clarity)`,
                fill: (segment.confidence || 1) < CLARITY_THRESHOLD ? "#ef4444" : "#22c55e"
            }));

        const totalWords = data.length;
        const lowClarityWords = data.filter(d => d.lowClarity).length;
        const averageConfidence = data.reduce((sum, d) => sum + d.confidence, 0) / totalWords;

        return {
            clarityData: data,
            stats: {
                totalWords,
                lowClarityWords,
                averageConfidence,
                clarityScore: ((totalWords - lowClarityWords) / totalWords) * 100
            }
        };
    }, [segments]);

    if (!segments?.length) return null;

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload?.[0]) return null;

        return (
            <div className={styles.customTooltip}>
                <p className={styles.tooltipText}>{payload[0].payload.tooltipContent}</p>
            </div>
        );
    };

    const MetricCard = ({ icon, label, value, unit = '' }) => (
        <div className={styles.metricCard}>
            {icon}
            <div className={styles.metricContent}>
                <span className={styles.metricLabel}>{label}</span>
                <span className={styles.metricValue}>
                    {value}{unit}
                </span>
            </div>
        </div>
    );

    return (
        <div className={styles.analysisSection}>
            <div className={styles.titleContainer}>
                <InfoModal type="clarity" />
            </div>
            <div className={styles.metricsGrid}>
                <MetricCard
                    icon={<Mic className={styles.metricIcon} />}
                    label="Average Clarity"
                    value={(stats.averageConfidence * 100).toFixed(1)}
                    unit="%"
                />
                <MetricCard
                    icon={<AlertCircle className={styles.metricIcon} />}
                    label="Low Clarity Words"
                    value={stats.lowClarityWords}
                />
                <MetricCard
                    icon={<BarChart3 className={styles.metricIcon} />}
                    label="Overall Score"
                    value={stats.clarityScore.toFixed(1)}
                    unit="%"
                />
            </div>

            <div className={styles.chartSection}>
                <ResponsiveContainer width="100%" height={300}>
                    <ScatterChart
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="index"
                            name="Word Position"
                            label={{
                                value: 'Word Position',
                                position: 'bottom',
                                offset: -10,
                                style: { fill: '#64748b' }
                            }}
                            tick={{ fill: '#64748b' }}
                        />
                        <YAxis
                            dataKey="confidence"
                            name="Clarity"
                            domain={[0, 1]}
                            label={{
                                value: 'Clarity Score',
                                angle: -90,
                                position: 'insideLeft',
                                offset: 10,
                                style: { fill: '#64748b' }
                            }}
                            tick={{ fill: '#64748b' }}
                        />
                        <ZAxis range={[100]} />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine
                            y={CLARITY_THRESHOLD}
                            stroke="#f59e0b"
                            strokeDasharray="3 3"
                            label={{
                                value: "Clarity threshold",
                                position: 'right',
                                fill: '#f59e0b'
                            }}
                        />
                        <Scatter
                            data={clarityData}
                            fillOpacity={1}
                            fill={(entry) => entry.fill}
                        />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>

            <div className={styles.legend}>
                <div className={styles.legendItem}>
                    <span className={`${styles.clarityDot} ${styles.greenDot}`}></span>
                    <span>High Clarity Words</span>
                </div>
                <div className={styles.legendItem}>
                    <span className={`${styles.clarityDot} ${styles.redDot}`}></span>
                    <span>Low Clarity Words</span>
                </div>
                <div className={styles.legendItem}>
                    <span className={styles.thresholdLine}></span>
                    <span>Clarity threshold ({CLARITY_THRESHOLD * 100}%)</span>
                </div>
            </div>
        </div>
    );
};

export default ClarityAnalysis;